<script lang="ts">
	import { Lightshow, Scene } from "luxedo-data"
	import { BackButton } from "misc-ui"
	import { ShowOverviewController } from "../ShowOverviewController"
	import LightshowPreviewTracks from "./LightshowPreviewTracks.svelte"
	import LightshowPreviewBreak from "./LightshowPreviewBreak.svelte"
	import { Responsive } from "responsive"
	import type {
		LightshowSceneTrack,
		LightshowBreakTrack,
		LightshowTrack,
	} from "./lightshow-preview-track-types"
	import { ShowPreviewVideo } from "luxedo-comps"

	interface Props {
		show: Lightshow
		isOpen: boolean
	}

	let { show, isOpen }: Props = $props()

	let isMobile = $derived(Responsive.isMobile)
	let tracks = $derived(convertBlocksToTracks(show))

	function convertBlocksToTracks(lightshow: Lightshow): LightshowTrack[] {
		return lightshow.getScenesInOrder().map((item) => {
			if (item instanceof Scene) {
				// LightshowSceneTrack
				return {
					mediaType: "scene",
					url: item.src.preview!,
					name: item.name,
					thumbnail: item.src.thumbnail,
				} as LightshowSceneTrack
			} else {
				// LightshowBreakTrack
				return {
					mediaType: "break",
					duration: item.duration,
					name: `Break ${Math.ceil(item.duration / 60)}min`,
				} as LightshowBreakTrack
			}
		})
	}

	let currentTrackIndex = $state(0)
	let isPlaying = $state(false)
	let breakComponent: LightshowPreviewBreak | undefined = $state()

	let videoElem: HTMLVideoElement = $state()

	// Get current track
	let currentTrack = $derived(tracks[currentTrackIndex])
	let totalTracks = $derived(tracks.length)
	let breakRemaining = $derived(
		currentTrack?.mediaType === "break" && breakComponent ? breakComponent.getBreakRemaining() : 0,
	)

	function resetPlayback() {
		if (!videoElem) return

		videoElem.pause()
		isPlaying = false
		currentTrackIndex = 0

		setTimeout(() => {
			if (!videoElem) return
			videoElem.currentTime = 0
		}, 500)
	}

	function jumpToTrack(trackIndex: number) {
		if (trackIndex < 0 || trackIndex >= totalTracks) return

		currentTrackIndex = trackIndex
		isPlaying = false

		if (currentTrack?.mediaType === "scene") {
			setTimeout(() => {
				if (videoElem && isOpen) {
					videoElem.currentTime = 0
					videoElem.play()
					isPlaying = true
				}
			}, 100)
		} else if (currentTrack?.mediaType === "break") {
			isPlaying = true
		}
	}

	function nextTrack() {
		if (currentTrackIndex < totalTracks - 1) {
			currentTrackIndex += 1

			if (currentTrack?.mediaType === "scene") {
				setTimeout(() => {
					if (videoElem && isOpen) {
						videoElem.play()
						isPlaying = true
					}
				}, 100)
			} else if (currentTrack?.mediaType === "break") {
				isPlaying = true
			}
		} else {
			// End of playlist
			isPlaying = false
			currentTrackIndex = 0
		}
	}

	function onVideoEnded() {
		nextTrack()
	}

	function onVideoPlay() {
		isPlaying = true
	}

	function onVideoPause() {
		isPlaying = false
	}

	function onBreakEnd() {
		nextTrack()
	}

	// use $effect instead of run (run is only for backwards compatibility)
	$effect(() => {
		if (!isOpen) {
			resetPlayback()
		} else if (isOpen && currentTrack) {
			// Auto-start when opened
			if (currentTrack.mediaType === "scene") {
				setTimeout(() => {
					if (videoElem) {
						videoElem.play()
						isPlaying = true
					}
				}, 100)
			} else if (currentTrack.mediaType === "break") {
				isPlaying = true
			}
		}
	})
</script>

<div class="track-info">
	<div class="track-meta">
		Track {currentTrackIndex + 1} of {totalTracks}
		{#if currentTrack?.mediaType === "break"}
			({breakRemaining}s)
		{/if}
	</div>
	{#if currentTrack}
		<div class="track-name">{currentTrack.name}</div>
	{/if}
</div>

<!-- Video or Break Display -->
<div class="main-content" class:isMobile>
	{#if currentTrack?.mediaType === "scene"}
		<ShowPreviewVideo
			{isOpen}
			src={currentTrack.url}
			onStop={onVideoEnded}
			onPlay={onVideoPlay}
			onPause={onVideoPause}
			errorMessage="Unable to load the preview for lightshow."
			bind:videoElement={videoElem}
		/>
	{:else if currentTrack?.mediaType === "break"}
		<LightshowPreviewBreak
			bind:this={breakComponent}
			duration={currentTrack.duration}
			isActive={isPlaying}
			{onBreakEnd}
		/>
	{/if}
</div>

<!-- Thumbnail Sidebar -->
<LightshowPreviewTracks {currentTrackIndex} {isPlaying} {jumpToTrack} {tracks} />

<style>
	/* Main content area - video/break content */
	.main-content {
		position: absolute;
		top: 3.5rem;
		left: 0;
		right: 20%;
		bottom: 0;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.main-content.isMobile {
		right: 0;
	}

	/* Track Info - top center */
	.track-info {
		position: absolute;
		top: 0.25rem;
		left: 50%;
		transform: translateX(-50%);
		color: white;
		padding: 0.25rem 0.75rem;
		border-radius: 0.75rem;
		font-size: var(--text-small);
		text-align: center;
		min-width: 11rem;
		z-index: 10;
	}

	.track-meta {
		font-size: 0.9em;
		opacity: 0.9;
	}

	.track-name {
		margin-top: 0.25rem;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: 19rem;
	}
</style>
